import { nanoid } from "nanoid";
import { request } from "../request";
import {
  FlowModuleTemplateType,
  ModuleInputKeyEnum,
  ModuleIOValueTypeEnum,
  ModuleItemType,
  ModuleTemplateTypeEnum,
} from "@packages/flow-core/module";
import {
  FlowNodeInputTypeEnum,
  FlowNodeTypeEnum,
} from "@packages/flow-core/module/node/constants";
import { IPluginFlow } from "./plugin";
import PluginModuleIcon from "../../assets/plugin-module.png";

export const NON_SENSITIVE_FILE_UPLOAD_URL =
  "/api/insnail-file-service/auth-api/publicFile?serviceNo=20";

export enum DatasetStructure {
  /** 普通知识库 */
  Normal = 1,
  /** 高级知识库 */
  Advance = 2,
  /** 智能知识库 */
  Smart = 3,
}

export enum InterruptConversationEnum {
  /** 不打断 */
  NoInterrupt,
  /** 打断 */
  Interrupt,
}

export function getModuleListService() {
  return request<API.BaseResponse<FlowModuleTemplateType[]>>(
    "/api/insnail-flow-platform/module/management/listAll"
  );
}

export type FlowApp = {
  id?: number | string;
  avatar?: string;
  name: string;
  description: string;
  modules: ModuleItemType[];
  interruptOldOnNew?: InterruptConversationEnum;
  createdBy?: string;
  createdGmt?: string;
  modifiedBy?: string;
  modifiedGmt?: string;
  updateTime?: string;
  rootModuleId?: string;
};

export function saveFlowAppService(data: FlowApp) {
  return request<API.BaseResponse<void>>(
    "/api/insnail-flow-platform/flow/management",
    {
      method: "POST",
      data,
    }
  );
}

export function getLLMDataService() {
  return request<API.BaseResponse<{ title: string; value: string }[]>>(
    "/api/insnail-flow-platform/module/management/aiModelList"
  );
}

export function runFlowAppService(
  flowId: number | string,
  data: Record<string, any>
) {
  return request(`/api/insnail-flow-platform/flow/runtime/test/${flowId}`, {
    method: "POST",
    data,
    responseType: "stream",
  });
}

export function getFlowAppService(flowId: string | number) {
  return request<API.BaseResponse<FlowApp>>(
    `/api/insnail-flow-platform/flow/management/${flowId}`
  );
}

export function clearChatHistoryService(chatId: string) {
  return request<API.BaseResponse>(
    `/api/insnail-flow-platform/flow/runtime/test/clear-chat/${chatId}`,
    {
      method: "PUT",
    }
  );
}

export function getSystemVariableService() {
  return request<API.BaseResponse<{ title: string; value: string }[]>>(
    "/api/insnail-flow-platform/module/management/system-variables"
  );
}

export type FlowPluginModuleTemplateType = Omit<
  FlowModuleTemplateType,
  "templateType" | "flowType"
> & {
  templateType: ModuleTemplateTypeEnum.pluginCall;
  flowType: FlowNodeTypeEnum.pluginModule;
};

const getInitValue = (valueType?: `${ModuleIOValueTypeEnum}`) => {
  switch (valueType) {
    case "bool": {
      return "false";
    }
    default: {
      return "";
    }
  }
};

/**
 * 将现有插件转化为模板支持拖入
 * @param plugins
 * @returns
 */
export function getUsablePlugins(
  plugins: IPluginFlow[]
): FlowPluginModuleTemplateType[] {
  return plugins.map((plugin) => ({
    ...plugin,
    id: nanoid(), // 绘制 id
    avatar: PluginModuleIcon,
    templateType: ModuleTemplateTypeEnum.pluginCall,
    flowType: FlowNodeTypeEnum.pluginModule,
    inputs: [
      {
        key: "switch",
        label: "是否运行",
        type: "target",
        valueType: "any",
        required: false,
        description: "节点默认运行，如果连接该参数，则在该参数有值时才运行。",
        showTargetInApp: true,
        showTargetInPlugin: true,
        edit: false,
        connected: false,
        multiple: false,
        hideInApp: false,
        hideInPlugin: false,
      },
      // 隐藏的插件 id
      {
        key: ModuleInputKeyEnum.selectPlugin,
        label: "",
        type: FlowNodeInputTypeEnum.hidden,
        valueType: ModuleIOValueTypeEnum.string,
        value: plugin.id!,
        required: true,
        hideInApp: true,
        hideInPlugin: true,
        hidden: true,
      },
      ...(plugin.modules
        .find((m) => m.flowType === FlowNodeTypeEnum.pluginInput)
        ?.inputs?.map((input) => ({
          ...input,
          value:
            input?.value !== void 0 && input?.value !== ""
              ? input?.value
              : getInitValue(input.valueType),
        })) ?? []),
    ],
    outputs: [
      {
        key: "finish",
        label: "模块执行结束",
        description: "模块执行结束时触发",
        required: false,
        targets: [],
        type: "source",
        valueType: "bool",
      },
      ...(plugin.modules.find(
        (m) => m.flowType === FlowNodeTypeEnum.pluginOutput
      )?.outputs ?? []),
    ],
  }));
}

/**
 * 获取知识库全部数据
 * @description 用于知识库节点外部渲染
 * @param structure
 * @returns
 */
export function getKnowledgeDataService(
  structure: DatasetStructure = DatasetStructure.Normal
) {
  return request<API.BaseResponse<{ title: string; value: string }[]>>(
    "/api/insnail-flow-platform/module/management/knowledgeBaseList",
    {
      method: "GET",
      params: { structure },
    }
  );
}

/**
 * 上传文件接口
 * @param file
 * @returns
 */
export function postUploadFile(file: File) {
  const formData = new FormData();
  formData.append("file", file);
  return request<API.BaseResponse<{ url: string }>>(
    NON_SENSITIVE_FILE_UPLOAD_URL,
    {
      method: "POST",
      data: formData,
    }
  );
}

export type KnowledgeField = {
  fieldName: string;
  needEmbedding: boolean;
  type: "TEXT" | "NUMBER" | "DATE";
};
export function getKnowledgeFieldsService(baseId: string) {
  return request<API.BaseResponse<KnowledgeField[]>>(
    "/api/knowledge-base-management/knowledge/findFieldByBaseId",
    {
      params: { baseId },
    }
  );
}
